/**
 * THE SESSION CREDENTIAL, CLIENT SIDE (th-vfii1).
 *
 * One leaf module owns the session bearer the SPA holds: where it lives
 * (`localStorage.token`), how a request carries it (the `Authorization`
 * header — never a URL), how a renewed one is adopted, and what a dead one
 * does to the tab. Both API clients route through it — `baseFetch`
 * (`services/apiClient.js`, v2) and the legacy axios instance
 * (`router/api.js`, v1, via `installSessionInterceptors`) — and so do the map's
 * vector tiles (`composables/mapHost/dataLayer/tileHandler.js`). It imports nothing from
 * the app but two leaves, the import-free `constants/workspacePath.js` and
 * `services/showcaseLane.js`, so every one of those can import it without a cycle.
 *
 * The server contract (BACK_END §"Session Authentication"):
 *   - a bearer past half its 30-day life comes back renewed in the
 *     `X-Session-Token` response header → `adoptRenewedSessionToken`;
 *   - a dead bearer (expired, revoked by sign-out or an email change, or any
 *     pre-th-vfii1 token) is answered
 *     `401 { error: "session_expired" }` → `handleSessionExpired`.
 *
 * That 401 is the ONLY answer that ends a session (th-10zrs). A 5xx, a
 * network failure or a timeout says nothing about the bearer, so nothing on
 * the client drops it for one: `isTransientFailure` names what is worth
 * retrying, and the boot identity check (`stores/auth.js#getCurrentUser`)
 * retries whoami on it rather than signing the member out.
 *
 * The WebSocket is NOT authenticated from here: its credential is an HttpOnly
 * cookie the server sets on whoami (page script never sees it).
 *
 * The same three transports honor the one departure gate (th-0qphb): once an
 * identity change commits the page to a full page load (`leavePage`), no
 * request leaves it — see `leavePage` below.
 */

import { WORKSPACE_PATH } from "@/constants/workspacePath";
import { laneHeaders } from "@/services/showcaseLane";

export const SESSION_TOKEN_KEY = "token";
export const RENEWAL_HEADER = "X-Session-Token";
export const SESSION_EXPIRED = "session_expired";
export const SIGN_IN_PATH = "/sign-in";

const LAST_USER_KEY = "tower_hunt_last_user_id";

export function readSessionToken() {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function adoptSessionToken(token) {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function clearSessionToken() {
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

/**
 * The headers a request needs to carry `token` (default: the held bearer), or
 * none for a guest, followed by the showcase lane's (`X-Showcase-Pass` while
 * the tour player holds a pass, th-mpimer.17). Spread it into a header bag.
 */
export function authorizationHeaders(token = readSessionToken()) {
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...laneHeaders() };
}

/** The bearer inside an `Authorization` value (`"Bearer x"` → `"x"`), or null. */
export function bearerOf(authorization) {
  if (typeof authorization !== "string") return null;
  const [scheme, token] = authorization.split(" ");
  return scheme === "Bearer" && token ? token : null;
}

/**
 * Read one header off a Fetch `Headers`, an axios `AxiosHeaders`, or a plain
 * object (axios lowercases response header names; tests pass plain objects).
 */
export function readHeader(headers, name) {
  if (!headers) return null;
  if (typeof headers.get === "function") return headers.get(name) ?? null;
  return headers[name] ?? headers[name.toLowerCase()] ?? null;
}

/**
 * Adopt the renewed bearer a response handed back — but ONLY while the bearer
 * that request carried is still the one this tab holds. A sign-out or a sign-in
 * that landed while the request was in flight must never be undone by a late
 * renewal of the identity it replaced.
 *
 * @returns {boolean} whether a renewal was adopted
 */
export function adoptRenewedSessionToken(renewed, sentToken) {
  if (!renewed || !sentToken) return false;
  if (readSessionToken() !== sentToken) return false;
  adoptSessionToken(renewed);
  return true;
}

export function isSessionExpired(status, code) {
  return status === 401 && code === SESSION_EXPIRED;
}

/**
 * The HTTP status a failed request carried, from either client's error shape:
 * axios puts it on `error.response.status`, `baseFetch` on `error.status`.
 * `undefined` when no response arrived at all (network down, timeout, abort).
 */
function failureStatus(error) {
  return error?.response?.status ?? error?.status;
}

/**
 * Whether a failed request is the server saying the held bearer is dead
 * (th-10zrs). The ONE failure that ends a session, read off either client's
 * error: axios carries the body on `error.response.data`, `baseFetch` lifts
 * its `error` key onto `error.code`.
 */
export function isSessionExpiredFailure(error) {
  const code = error?.response ? error.response.data?.error : error?.code;
  return isSessionExpired(failureStatus(error), code);
}

/**
 * Whether a failed request is worth asking again (th-10zrs): no response at
 * all (network down, a timeout — axios reports both without one), a 5xx (a
 * deploy's 502/503, a transient 500), a 408 or a 429. Neither this nor any
 * other failure short of `isSessionExpiredFailure` ends a session: a caller
 * that cannot recover keeps the bearer and tries again later.
 */
export function isTransientFailure(error) {
  const status = failureStatus(error);
  if (status === undefined || status === null || status === 0) return true;
  return status >= 500 || status === 408 || status === 429;
}

// Whether this page has committed to a full page load. Page-lifetime state,
// like everything the page holds: the next document starts with it false.
let leaving = false;

/**
 * THE PAGE IS LEAVING (th-0qphb): close the request gate NOW, and run `exit`
 * (the `location.reload` / `location.assign` that ends this page) `afterMs`
 * later — on a later task even at 0, so the identity change the caller makes
 * right after this call lands first.
 *
 * WHY A GATE AND NOT A FASTER EXIT. An identity change tells every user-scoped
 * store at once — the `authTransition` listeners and every watcher on the
 * current user — and many of them refetch as the new identity. On a page that
 * is about to be replaced, each of those reads is paid for (the server runs
 * the whole query, and `/filtered_data/query` captures, and charges, at read
 * time) and rendered for nobody. No exit is fast enough to prevent that: the
 * listeners run synchronously inside the dispatch, and a page keeps running,
 * and sending, until the navigation it started commits. So the one mechanism
 * is at the one seam every request crosses — both API clients and the vector
 * tiles already read the bearer here — and it holds whatever issued the read.
 * Clearing is untouched: listeners still drop what they cached under the old
 * identity (the band caches live in `localStorage` and would otherwise outlive
 * the page); only the refetch never leaves.
 *
 * The departure is irrevocable by construction: `exit` is already scheduled
 * when the gate closes, and nothing in the app vetoes an unload (no
 * `beforeunload` handler cancels one).
 *
 * @param {() => void} exit the full page load that ends this page
 * @param {{ afterMs?: number }} [options]
 */
export function leavePage(exit, { afterMs = 0 } = {}) {
  leaving = true;
  setTimeout(exit, afterMs);
}

/**
 * Whether a request built NOW stays unsent: the page is leaving, and the
 * request is not a `keepalive` one. `keepalive` is the Fetch standard's own
 * declaration that a request is written to outlive its page — a teardown
 * whose response nobody on the page needs (the sign-out revoke, a device's
 * push revocation) — so it still goes out.
 *
 * @param {{ keepalive?: boolean }} [init] the request's options
 */
export function isRequestWithheld(init) {
  return leaving && !init?.keepalive;
}

/**
 * What a withheld request resolves to: nothing, ever. The page is gone before
 * any caller could render an answer, and a settled failure would be worse
 * than silence — it would paint error states and toasts for a moment on the
 * way out, and a caller that retries transient failures (a failure with no
 * status reads as transient) would ask again.
 */
export function neverAnswered() {
  return new Promise(() => {});
}

/**
 * Drop this tab's identity: the bearer, the last-user marker, and the
 * `authTransition` every user-scoped store listens for so it clears what it
 * cached under the departing identity. Shared by sign-out
 * (`stores/auth.js#signOut`) and session expiry, so the two cannot drift.
 */
export function dropSessionIdentity() {
  const previousUserId = localStorage.getItem(LAST_USER_KEY);
  if (previousUserId && previousUserId !== "guest") {
    localStorage.removeItem(LAST_USER_KEY);
    window.dispatchEvent(
      new CustomEvent("authTransition", {
        detail: { type: "signOut", previousUserId },
      }),
    );
  }
  clearSessionToken();
}

/**
 * Wire an axios instance (the legacy v1 client, `router/api.js`) to carry the
 * session exactly as `baseFetch` does, so the two clients cannot disagree:
 *
 *   - request: the bearer is read HERE at send time, never frozen into
 *     `defaults` at import, so a sign-in, sign-out or renewal applies to the
 *     next request. On a leaving page the request is never sent at all
 *     (`isRequestWithheld`, th-0qphb);
 *   - response: a renewal is adopted, and a `401 session_expired` goes to
 *     `handleSessionExpired` with the bearer the request carried, before the
 *     caller's own rejection handler runs.
 */
export function installSessionInterceptors(client) {
  client.interceptors.request.use((config) => {
    if (isRequestWithheld(config)) return neverAnswered();
    const { Authorization } = authorizationHeaders();
    if (Authorization) config.headers.Authorization = Authorization;
    return config;
  });

  const sentBearer = (response) => bearerOf(readHeader(response.config?.headers, "Authorization"));
  const adoptRenewal = (response) =>
    adoptRenewedSessionToken(readHeader(response.headers, RENEWAL_HEADER), sentBearer(response));

  client.interceptors.response.use(
    (response) => {
      adoptRenewal(response);
      return response;
    },
    (error) => {
      const response = error?.response;
      if (response) {
        adoptRenewal(response);
        if (isSessionExpired(response.status, response.data?.error)) handleSessionExpired(sentBearer(response));
      }
      return Promise.reject(error);
    },
  );

  return client;
}

/**
 * The server refused the held bearer. The tab stops being that identity and
 * the member lands somewhere that works, never on a shell that still thinks
 * it is signed in while every request fails. It always drops the identity,
 * then:
 *
 *   - on the workspace (`/`): go to sign-in, carrying the query string so an
 *     invitation link's token survives the detour;
 *   - on any other page (help, legal): reload it as a guest — it works signed out, and a route that needs a
 *     session sends the guest to sign-in through the router's own guard.
 *
 * Every exit is a full page load, which tears down all in-memory state the
 * departing identity owned — the same reason sign-out reloads. It leaves
 * through `leavePage`, declared BEFORE the identity drop, so nothing the drop's
 * `signOut` transition sets refetching is ever sent (th-0qphb).
 *
 * A burst of requests all answer 401 at once (every panel refetches on boot);
 * a page that is already leaving does not leave again, so the tab leaves
 * exactly once.
 *
 * A 401 speaks only for the bearer its request CARRIED (`sentToken`, the same
 * comparison `adoptRenewedSessionToken` makes). When the tab has since moved to
 * a newer bearer (an email change reissues one and revokes the old, th-m1z22),
 * a request that left on the old one says nothing about the tab, which keeps
 * going. A caller that cannot name the bearer, or a tab that holds none, leaves
 * as before.
 *
 * @param {string|null} [sentToken] the bearer the failed request carried
 */
export function handleSessionExpired(sentToken) {
  if (leaving) return;

  const held = readSessionToken();
  if (sentToken && held && sentToken !== held) return;

  const signInUrl = `${SIGN_IN_PATH}${window.location.search}`;
  // A dead token entering at the old `/` spelling still lands on sign-in.
  const { pathname } = window.location;
  const onWorkspace = pathname === WORKSPACE_PATH || pathname === "/"; // lint:legacy-root-link
  leavePage(() => (onWorkspace ? window.location.assign(signInUrl) : window.location.reload()));

  dropSessionIdentity();
}
